/**
 * Seen Achievements View Component
 * Displays all achievements with cycling animation
 */

import React, { useMemo, useEffect, useState, useRef } from 'react'
import { Box } from '@mui/material'
import { SessionAchievementBadge } from '../../achievements/components/SessionAchievementBadge'
import { Achievement, TieredAchievement } from '../../achievements/types/achievementTypes'
import { SessionAchievementDisplay } from '../../achievements/types/achievementTypes'
import { AchievementLevel } from '../../achievements/types/achievementTypes'
import { BADGE_SIZE_PX, GAP_PX, STEP_SIZE, MAX_VISIBLE_ACHIEVEMENTS } from './achievementAnimationConstants'
import { AchievementCardBackside } from './AchievementCardBackside'

// Animation constants (same as UnseenAchievementsView)
const FLIP_DURATION = 600 // ms for flip animation
const SPIN_DURATION = 4000 // ms for one full rotation (slow spin)
const STAGGER_DELAY = 300 // ms delay between each card flip

export interface SeenAchievementsViewProps {
  allAchievements: Achievement[]
  isReportReady: boolean
  onNavigate: () => void
}

/**
 * Convert AchievementLevel to string for SessionAchievementBadge
 */
const getTierFromAchievement = (achievement: Achievement): 'Bronze' | 'Silver' | 'Gold' => {
  if (achievement.type === 'tiered') {
    const { level } = achievement as TieredAchievement
    if (level === AchievementLevel.GOLD) return 'Gold'
    if (level === AchievementLevel.SILVER) return 'Silver'
    return 'Bronze'
  }
  return 'Bronze'
}

/**
 * SeenAchievementsView - Displays all achievements with cycling animation
 */
export const SeenAchievementsView: React.FC<SeenAchievementsViewProps> = ({ allAchievements }) => {
  const [selectedAchievementId, setSelectedAchievementId] = useState<string | null>(null)
  const selectedAchievementIdRef = useRef<string | null>(null)
  const [currentBatchOffset, setCurrentBatchOffset] = useState(0)
  // Flip/spin animation state (same as UnseenAchievementsView)
  const [flippedAchievements, setFlippedAchievements] = useState<Set<string>>(new Set())
  const [spinningAchievements, setSpinningAchievements] = useState<Set<string>>(new Set())
  const timeoutRefsRef = useRef<Map<string, NodeJS.Timeout>>(new Map())

  // Determine which 4 achievements to display from current batch
  const achievementsToDisplay = useMemo(() => {
    const startIndex = currentBatchOffset * MAX_VISIBLE_ACHIEVEMENTS
    const endIndex = startIndex + MAX_VISIBLE_ACHIEVEMENTS
    return allAchievements.slice(startIndex, endIndex)
  }, [allAchievements, currentBatchOffset])

  // Reset batch offset and animation state when achievements list changes
  useEffect(() => {
    setCurrentBatchOffset(0)
    setSelectedAchievementId(null)
    selectedAchievementIdRef.current = null
    // Reset flip/spin state
    setFlippedAchievements(new Set())
    setSpinningAchievements(new Set())
    // Clear all timeouts
    timeoutRefsRef.current.forEach((timeout) => clearTimeout(timeout))
    timeoutRefsRef.current.clear()
  }, [allAchievements.length])

  // Handle flip/spin animation for current batch (same logic as UnseenAchievementsView)
  useEffect(() => {
    if (achievementsToDisplay.length === 0) return

    // Clear any existing timeouts
    timeoutRefsRef.current.forEach((timeout) => clearTimeout(timeout))
    timeoutRefsRef.current.clear()

    // Flip each card with a stagger delay
    achievementsToDisplay.forEach((achievement, index) => {
      const flipDelay = index * STAGGER_DELAY
      const flipKey = `flip-${achievement.id}`

      // Schedule flip
      const flipTimer = setTimeout(() => {
        setFlippedAchievements((prev) => new Set(prev).add(achievement.id))

        // Start spinning after flip completes
        const spinTimer = setTimeout(() => {
          setSpinningAchievements((prev) => new Set(prev).add(achievement.id))
        }, FLIP_DURATION)
        timeoutRefsRef.current.set(`spin-${achievement.id}`, spinTimer)
      }, flipDelay)
      timeoutRefsRef.current.set(flipKey, flipTimer)
    })

    // Cleanup function
    const currentTimeouts = timeoutRefsRef.current
    return () => {
      currentTimeouts.forEach((timeout) => clearTimeout(timeout))
      currentTimeouts.clear()
    }
  }, [achievementsToDisplay])

  // Handle achievement click
  const handleAchievementClick = (achievementId: string) => {
    selectedAchievementIdRef.current = achievementId
    setSelectedAchievementId(achievementId)
  }

  // Auto-cycling animation: loop through current batch, then move to next batch
  useEffect(() => {
    if (allAchievements.length === 0) return

    let currentIndexInBatch = -1 // Start at -1 so first cycle shows index 0

    const cycleAchievement = () => {
      // Increment the index
      currentIndexInBatch = currentIndexInBatch + 1

      // Get the current batch info
      const currentBatchStart = currentBatchOffset * MAX_VISIBLE_ACHIEVEMENTS
      const achievementsInCurrentBatch = Math.min(MAX_VISIBLE_ACHIEVEMENTS, allAchievements.length - currentBatchStart)

      // Check if we've completed the current batch (shown all achievements in this batch)
      if (currentIndexInBatch >= achievementsInCurrentBatch) {
        // We've completed the current batch, check if there are more achievements
        const nextBatchStartIndex = (currentBatchOffset + 1) * MAX_VISIBLE_ACHIEVEMENTS
        if (nextBatchStartIndex < allAchievements.length) {
          // Move to next batch
          setCurrentBatchOffset((prev) => prev + 1)
          return // Exit early, the useEffect will re-run with new batch
        } else {
          // We've shown all achievements, loop back to the first batch
          setCurrentBatchOffset(0)
          return // Exit early, the useEffect will re-run with first batch
        }
      }

      // Get the achievement from the current batch
      const achievementIndex = currentBatchStart + currentIndexInBatch

      // Safety check
      if (achievementIndex >= allAchievements.length) {
        // Reset to first batch if we somehow go out of bounds
        setCurrentBatchOffset(0)
        return
      }

      const achievement = allAchievements[achievementIndex]
      const achievementId = achievement.id

      // Update ref first (for polling performance)
      selectedAchievementIdRef.current = achievementId
      // Then update state (triggers re-render for animation)
      setSelectedAchievementId(achievementId)
    }

    // Start cycling after initial delay, then continue every 3 seconds
    const startTimer = setTimeout(() => {
      cycleAchievement() // Show first achievement
    }, 1000)

    const interval = setInterval(cycleAchievement, 3000) // Cycle every 3 seconds

    return () => {
      clearTimeout(startTimer)
      clearInterval(interval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allAchievements.length, currentBatchOffset])

  // Calculate positions for achievements in the row (same as UnseenAchievementsView)
  const achievementPositions = useMemo(() => {
    const positions: Record<string, { x: number; index: number }> = {}
    achievementsToDisplay.forEach((achievement, index) => {
      const centerIndex = Math.floor(achievementsToDisplay.length / 2)
      const offsetFromCenter = (index - centerIndex) * STEP_SIZE
      positions[achievement.id] = {
        x: offsetFromCenter,
        index
      }
    })
    return positions
  }, [achievementsToDisplay])

  if (achievementsToDisplay.length === 0) {
    return null
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 3,
        position: 'relative',
        overflow: 'visible',
        flex: 1,
        minHeight: '300px',
        perspective: '1000px', // Enable 3D transforms
        perspectiveOrigin: 'center center',
        // Define keyframes for spin animation
        '@keyframes cardSpin': {
          '0%': {
            transform: 'rotateY(0deg)'
          },
          '100%': {
            transform: 'rotateY(360deg)'
          }
        }
      }}
    >
      {/* Container for all achievements - positioned at bottom row */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '20px', // Position at bottom (row 4)
          left: '50%',
          transform: 'translateX(-50%)',
          display: 'flex',
          gap: 2,
          justifyContent: 'center',
          alignItems: 'center',
          paddingX: 2,
          width: '100%',
          maxWidth: `calc(${achievementsToDisplay.length * BADGE_SIZE_PX}px + ${(achievementsToDisplay.length - 1) * GAP_PX}px + 64px)`,
          transformStyle: 'preserve-3d'
        }}
      >
        {achievementsToDisplay.map((achievement) => {
          const fullAchievement = achievement as Achievement
          const tier = getTierFromAchievement(fullAchievement)
          const position = achievementPositions[fullAchievement.id]
          const isFlipped = flippedAchievements.has(fullAchievement.id)
          const isSpinning = spinningAchievements.has(fullAchievement.id)
          const isSelected = selectedAchievementId === fullAchievement.id

          return (
            <Box
              key={fullAchievement.id}
              onClick={() => handleAchievementClick(fullAchievement.id)}
              sx={{
                position: 'relative',
                width: `${BADGE_SIZE_PX}px`,
                height: `${BADGE_SIZE_PX}px`,
                transform: `translate(${position.x}px, 0)`,
                transformStyle: 'preserve-3d',
                cursor: 'pointer',
                opacity: isSelected ? 1 : 0.8,
                flexShrink: 0,
                zIndex: isSelected ? 10 : 1,
                '&:hover': {
                  opacity: 1
                }
              }}
            >
              {/* Outer container: handles flip animation */}
              <Box
                sx={{
                  position: 'relative',
                  width: '100%',
                  height: '100%',
                  transformStyle: 'preserve-3d',
                  // Flip transform: 180deg (backside) -> 0deg (front)
                  transform: isFlipped ? 'rotateY(0deg)' : 'rotateY(180deg)',
                  transition: `transform ${FLIP_DURATION}ms ease-in-out`
                }}
              >
                {/* Inner container: handles spin animation */}
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    transformStyle: 'preserve-3d',
                    // Spin animation only when active
                    ...(isSpinning && {
                      animation: `cardSpin ${SPIN_DURATION}ms linear infinite`
                    })
                  }}
                >
                  {/* Backside of card (Intraction logo) */}
                  <Box
                    sx={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      top: 0,
                      left: 0,
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateY(180deg)',
                      transformStyle: 'preserve-3d'
                    }}
                  >
                    <AchievementCardBackside size="small" />
                  </Box>

                  {/* Front of card (Achievement badge) */}
                  <Box
                    sx={{
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      top: 0,
                      left: 0,
                      backfaceVisibility: 'hidden',
                      WebkitBackfaceVisibility: 'hidden',
                      transform: 'rotateY(0deg)',
                      transformStyle: 'preserve-3d'
                    }}
                  >
                    <SessionAchievementBadge
                      achievement={{
                        id: fullAchievement.id as unknown as SessionAchievementDisplay['id'],
                        tier: tier as SessionAchievementDisplay['tier'],
                        title: fullAchievement.name,
                        description: fullAchievement.description,
                        tooltip: fullAchievement.tooltip
                      }}
                      size="small"
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}
